import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import PaymentForm from '../components/payments/PaymentForm';
import { CreditCard, Check, Shield, Crown, User, Users, ArrowLeft, AlertCircle, Building, MapPin, Phone, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Prezzi abbonamenti - aggiornati a 1, 3, 5 anni
const PRICES = {
  base: {
    individual: { 1: 59, 3: 49, 5: 39 },
    family: { 1: 179, 3: 159, 5: 139 }
  },
  premium: {
    individual: { 1: 159, 3: 149, 5: 139 },
    family: { 1: 499, 3: 389, 5: 369 }
  }
};

const PLAN_DETAILS = {
  base: {
    name: 'Base',
    color: '#2A9FCB',
    icon: Shield,
    included: [
      'Tutte le prestazioni diagnostiche',
      'Topografia corneale',
      'Retinografia e fondo oculare',
      'OCT macula e nervo ottico',
      'Campo visivo',
      'Ecografia bulbare',
      'Visite di controllo illimitate',
      'Follow-up inclusi'
    ]
  },
  premium: {
    name: 'Premium',
    color: '#D4AF37',
    icon: Crown,
    included: [
      'Tutto del piano Base +',
      'Tutti i trattamenti avanzati',
      'Luce pulsata / LLLT',
      'Laser SLT, Iridotomia, Capsulotomia',
      'Intervento cataratta incluso',
      'Intervento strabismo incluso',
      'Farmaci e colliri inclusi',
      'Medico personale online',
      'Segreteria dedicata h24'
    ]
  }
};

export const Checkout = () => {
  const { packageId } = useParams(); // formato: base-individual o premium-family
  const navigate = useNavigate();
  
  // Parse packageId
  const [planType, billingTypeFromUrl] = packageId ? packageId.split('-') : ['base', 'individual'];
  
  // State per tipo abbonamento
  const [selectedPlan, setSelectedPlan] = useState(planType || 'base');
  const [billingType, setBillingType] = useState(billingTypeFromUrl || 'individual');
  const [duration, setDuration] = useState(1);
  
  // State per dati fattura
  const [billingData, setBillingData] = useState({
    firstName: '',
    lastName: '',
    fiscalCode: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    cap: '',
    province: '',
    // Dati aziendali (opzionali)
    isCompany: false,
    companyName: '',
    vatNumber: '',
    sdiCode: '',
    pec: ''
  });
  
  // State pagamento
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calcola il prezzo
  const monthlyPrice = PRICES[selectedPlan]?.[billingType]?.[duration] || 59;
  const totalMonths = duration * 12;
  const totalPrice = monthlyPrice * totalMonths;
  
  const planDetails = PLAN_DETAILS[selectedPlan];
  const PlanIcon = planDetails?.icon || Shield;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const required = ['firstName', 'lastName', 'fiscalCode', 'email', 'phone', 'address', 'city', 'cap', 'province'];
    for (const field of required) {
      if (!billingData[field]?.trim()) {
        toast.error(`Il campo ${field === 'firstName' ? 'Nome' : field === 'lastName' ? 'Cognome' : field === 'fiscalCode' ? 'Codice Fiscale' : field === 'cap' ? 'CAP' : field.charAt(0).toUpperCase() + field.slice(1)} è obbligatorio`);
        return false;
      }
    }
    if (billingData.isCompany && (!billingData.companyName || !billingData.vatNumber)) {
      toast.error('Per fattura aziendale inserire Ragione Sociale e P.IVA');
      return false;
    }
    if (!acceptTerms || !acceptPrivacy) {
      toast.error('Devi accettare i termini e la privacy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Mostra il form di pagamento
    setShowPayment(true);
  };

  const handlePaymentSuccess = (data) => {
    setPaymentSuccess(true);
    toast.success('🎉 Abbonamento attivato con successo!');
    setTimeout(() => {
      navigate('/dashboard/patient');
    }, 3000);
  };

  const handlePaymentError = (error) => {
    toast.error(error || 'Errore durante il pagamento');
  };

  return (
    <div className="min-h-screen" style={{background: '#F9FAFB'}}>
      <Navbar />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/abbonamenti')}
            className="flex items-center gap-2 mb-6 text-[#2A9FCB] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna agli abbonamenti
          </button>

          <h1 className="text-3xl sm:text-4xl font-bold text-center mb-2" style={{color: '#2A9FCB'}}>
            Attiva il tuo Abbonamento
          </h1>
          <p className="text-center text-[#2A9FCB]/70 mb-8">
            Compila i dati per la fatturazione e scegli il piano più adatto a te
          </p>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Colonna Sinistra: Selezione Piano */}
            <div className="lg:col-span-1 space-y-6">
              {/* Selezione Piano */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>1. Scegli il Piano</h2>
                
                <div className="space-y-3">
                  {Object.entries(PLAN_DETAILS).map(([key, plan]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPlan(key)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        selectedPlan === key ? 'shadow-lg' : 'hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: selectedPlan === key ? plan.color : '#E5E7EB',
                        background: selectedPlan === key ? `${plan.color}10` : 'white'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: plan.color}}>
                          <plan.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold" style={{color: plan.color}}>{plan.name}</h3>
                          <p className="text-xs text-gray-500">
                            da {PRICES[key].individual[5]}€/mese
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo: Individuale o Famiglia */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>2. Tipo Abbonamento</h2>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBillingType('individual')}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      billingType === 'individual' ? 'shadow-md' : ''
                    }`}
                    style={{
                      borderColor: billingType === 'individual' ? '#2A9FCB' : '#E5E7EB',
                      background: billingType === 'individual' ? '#e8f4fd' : 'white'
                    }}
                  >
                    <User className="w-6 h-6 mx-auto mb-2" style={{color: billingType === 'individual' ? '#2A9FCB' : '#9CA3AF'}} />
                    <span className={`text-sm font-medium ${billingType === 'individual' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>
                      Individuale
                    </span>
                  </button>
                  <button
                    onClick={() => setBillingType('family')}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      billingType === 'family' ? 'shadow-md' : ''
                    }`}
                    style={{
                      borderColor: billingType === 'family' ? '#2A9FCB' : '#E5E7EB',
                      background: billingType === 'family' ? '#e8f4fd' : 'white'
                    }}
                  >
                    <Users className="w-6 h-6 mx-auto mb-2" style={{color: billingType === 'family' ? '#2A9FCB' : '#9CA3AF'}} />
                    <span className={`text-sm font-medium ${billingType === 'family' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>
                      Famiglia (max 5)
                    </span>
                  </button>
                </div>
              </div>

              {/* Durata */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>3. Durata</h2>
                
                <div className="space-y-2">
                  {[1, 3, 5].map((years) => {
                    const price = PRICES[selectedPlan][billingType][years];
                    return (
                      <button
                        key={years}
                        onClick={() => setDuration(years)}
                        className={`w-full p-3 rounded-lg border-2 flex justify-between items-center transition-all ${
                          duration === years ? 'shadow-md' : ''
                        }`}
                        style={{
                          borderColor: duration === years ? '#2A9FCB' : '#E5E7EB',
                          background: duration === years ? '#e8f4fd' : 'white'
                        }}
                        data-testid={`duration-${years}-btn`}
                      >
                        <span className={`font-medium ${duration === years ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>
                          {years} {years === 1 ? 'Anno' : 'Anni'}
                          {years === 3 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Consigliato</span>}
                        </span>
                        <span className="font-bold" style={{color: duration === years ? '#2A9FCB' : '#6B7280'}}>
                          {price}€/mese
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Riepilogo */}
              <div className="bg-white rounded-2xl shadow-lg p-6" style={{background: planDetails?.color + '10'}}>
                <h2 className="text-lg font-bold mb-4" style={{color: planDetails?.color}}>Riepilogo</h2>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span>Piano:</span>
                    <span className="font-semibold">{planDetails?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tipo:</span>
                    <span className="font-semibold">{billingType === 'individual' ? 'Individuale' : 'Famiglia'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Durata:</span>
                    <span className="font-semibold">{duration} {duration === 1 ? 'anno' : 'anni'}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span>Prezzo mensile:</span>
                      <span className="font-bold text-lg" style={{color: planDetails?.color}}>{monthlyPrice}€</span>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  <p>* I prezzi sono IVA esclusa</p>
                  <p>* Pagamento mensile tramite addebito automatico</p>
                </div>
              </div>
            </div>

            {/* Colonna Destra: Form Dati */}
            <div className="lg:col-span-2">
              {paymentSuccess ? (
                /* Success State */
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-800 mb-2">Abbonamento Attivato!</h2>
                  <p className="text-gray-600 mb-4">
                    Il tuo abbonamento <strong>{planDetails?.name}</strong> è stato attivato con successo.
                  </p>
                  <div className="inline-block bg-amber-100 text-amber-800 px-3 py-1 rounded-lg text-sm mb-4">
                    🧪 Modalità Demo - Nessun addebito reale
                  </div>
                  <p className="text-sm text-gray-500">
                    Reindirizzamento alla dashboard in corso...
                  </p>
                </div>
              ) : showPayment ? (
                /* Payment Form */
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                      Pagamento
                    </h2>
                    <button
                      onClick={() => setShowPayment(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Modifica dati
                    </button>
                  </div>

                  {/* Riepilogo Ordine */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold mb-3" style={{color: planDetails?.color}}>
                      Riepilogo Ordine
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Piano {planDetails?.name} ({billingType === 'individual' ? 'Individuale' : 'Famiglia'})</span>
                        <span>{monthlyPrice}€/mese</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Durata: {duration} {duration === 1 ? 'anno' : 'anni'}</span>
                        <span>{totalMonths} mesi</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t" style={{color: planDetails?.color}}>
                        <span>Totale primo pagamento</span>
                        <span>{monthlyPrice}€</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * Addebito mensile di {monthlyPrice}€ per {totalMonths} mesi
                    </p>
                  </div>

                  {/* Intestatario */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <p className="text-sm text-gray-600">
                      <strong>Intestatario:</strong> {billingData.firstName} {billingData.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Email:</strong> {billingData.email}
                    </p>
                  </div>

                  {/* Payment Component */}
                  <PaymentForm
                    amount={monthlyPrice}
                    description={`Abbonamento ${planDetails?.name} - ${billingType === 'individual' ? 'Individuale' : 'Famiglia'} (${duration} ${duration === 1 ? 'anno' : 'anni'})`}
                    billingData={billingData}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    showPayPal={true}
                  />
                </div>
              ) : (
                /* Billing Data Form */
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                <h2 className="text-xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                  Dati per la Fatturazione
                </h2>

                {/* Toggle Persona/Azienda */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isCompany"
                      checked={billingData.isCompany}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded"
                      style={{accentColor: '#2A9FCB'}}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      <Building className="inline w-4 h-4 mr-1" />
                      Richiedo fattura aziendale
                    </span>
                  </label>
                </div>

                {/* Dati Aziendali (se selezionato) */}
                {billingData.isCompany && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-4">
                    <h3 className="font-semibold text-gray-700">Dati Aziendali</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">Ragione Sociale *</label>
                        <input
                          type="text"
                          name="companyName"
                          value={billingData.companyName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="Nome Azienda Srl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">Partita IVA *</label>
                        <input
                          type="text"
                          name="vatNumber"
                          value={billingData.vatNumber}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="IT12345678901"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">Codice SDI</label>
                        <input
                          type="text"
                          name="sdiCode"
                          value={billingData.sdiCode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="XXXXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">PEC</label>
                        <input
                          type="email"
                          name="pec"
                          value={billingData.pec}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="azienda@pec.it"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Dati Personali */}
                <div className="space-y-4 mb-6">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Dati Personali
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Nome *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={billingData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="Mario"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Cognome *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={billingData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="Rossi"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Codice Fiscale *</label>
                      <input
                        type="text"
                        name="fiscalCode"
                        value={billingData.fiscalCode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB] uppercase"
                        placeholder="RSSMRA80A01H501U"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        <Mail className="inline w-4 h-4 mr-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={billingData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="mario.rossi@email.com"
                        required
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        <Phone className="inline w-4 h-4 mr-1" />
                        Telefono *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={billingData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="+39 333 1234567"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Indirizzo */}
                <div className="space-y-4 mb-6">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Indirizzo di Fatturazione
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-1 text-gray-700">Indirizzo *</label>
                      <input
                        type="text"
                        name="address"
                        value={billingData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="Via Roma 123"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Città *</label>
                      <input
                        type="text"
                        name="city"
                        value={billingData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                        placeholder="Roma"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">CAP *</label>
                        <input
                          type="text"
                          name="cap"
                          value={billingData.cap}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="00100"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">Provincia *</label>
                        <input
                          type="text"
                          name="province"
                          value={billingData.province}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                          placeholder="RM"
                          maxLength={2}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Termini e Condizioni */}
                <div className="space-y-3 mb-6 p-4 bg-gray-50 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded"
                      style={{accentColor: '#2A9FCB'}}
                      data-testid="accept-terms"
                    />
                    <span className="text-sm text-gray-600">
                      Accetto i <a href="/termini" className="text-[#2A9FCB] underline">Termini e Condizioni</a> del servizio *
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded"
                      style={{accentColor: '#2A9FCB'}}
                      data-testid="accept-privacy"
                    />
                    <span className="text-sm text-gray-600">
                      Ho letto e accetto la <a href="/privacy" className="text-[#2A9FCB] underline">Privacy Policy</a> *
                    </span>
                  </label>
                </div>

                {/* Info Box Demo */}
                <div className="mb-6 p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">🧪 Modalità Demo Attiva</p>
                      <p>Il sistema di pagamento è in modalità test. Nessun addebito reale sarà effettuato. Potrai provare il flusso completo con carta di test.</p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full py-4 rounded-xl font-bold text-white text-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{background: planDetails?.color}}
                  data-testid="proceed-to-payment-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Procedi al Pagamento
                    </>
                  )}
                </button>

                <p className="text-xs text-center mt-4 text-gray-500">
                  🔒 I tuoi dati sono al sicuro e protetti con crittografia SSL
                </p>
              </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;
